/**
 * P0 Sprint — Betalingen sectie binnen factuurdetail.
 *
 * Toont:
 *   - Factuurtotaal / totaal betaald / openstaand bedrag / betaalstatus
 *   - Lijst van actieve betalingen + gevoide betalingen (apart blok)
 *   - Knop "Betaling registreren" (Manager+ / Owner / Admin / Webshop manager)
 *   - Per-rij acties Bewerken / Voiden met verplichte reden (Manager+)
 *
 * Medewerker = alleen-lezen.
 *
 * Backend endpoints (zie /app/backend/admin_invoice_payments.py):
 *   GET   /api/admin/sales-invoices/{invoice_id}/payments
 *   POST  /api/admin/sales-invoices/{invoice_id}/payments
 *   PATCH /api/admin/sales-invoices/{invoice_id}/payments/{payment_id}
 *   POST  /api/admin/sales-invoices/{invoice_id}/payments/{payment_id}/void
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  CreditCard, Plus, Loader2, AlertTriangle, Edit2, Ban, CheckCircle2, Receipt,
} from "lucide-react";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { Textarea } from "../../ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../../ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../ui/select";
import { adminApi, useAuth } from "../../../context/AuthContext";

const PAYMENT_METHODS = [
  { value: "bank", label: "Bank / IBAN" },
  { value: "contant", label: "Contant" },
  { value: "pin", label: "PIN" },
  { value: "stripe", label: "Stripe" },
  { value: "sumup", label: "SumUp" },
  { value: "overboeking", label: "Overboeking" },
  { value: "anders", label: "Anders…" },
];

function methodLabel(value, other) {
  if (value === "anders") return other ? `Anders — ${other}` : "Anders";
  const m = PAYMENT_METHODS.find((p) => p.value === value);
  return m?.label || value || "—";
}

function fmtEUR(value, currency = "EUR") {
  const num = Number(value || 0);
  return `${currency} ${num.toFixed(2).replace(".", ",")}`;
}
function fmtDate(iso) {
  if (!iso) return "—";
  return String(iso).slice(0, 10);
}

/**
 * Status-pillen voor automatische betaalstatus.
 */
export function PaymentStatusPill({ status, overpaid }) {
  if (overpaid) {
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] uppercase font-bold px-2 py-0.5 border bg-purple-50 text-purple-700 border-purple-200"
        data-testid="payment-status-overpaid"
      >
        Overbetaald
      </span>
    );
  }
  const map = {
    betaald: { label: "Betaald", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
    deels_betaald: { label: "Deels betaald", cls: "bg-amber-50 text-amber-800 border-amber-200" },
    open: { label: "Onbetaald", cls: "bg-gray-100 text-gray-700 border-gray-200" },
    verzonden: { label: "Onbetaald", cls: "bg-gray-100 text-gray-700 border-gray-200" },
    concept: { label: "Concept", cls: "bg-gray-100 text-gray-700 border-gray-200" },
    geannuleerd: { label: "Geannuleerd", cls: "bg-red-50 text-red-700 border-red-200" },
  };
  const info = map[status] || { label: status || "—", cls: "bg-gray-100 text-gray-700 border-gray-200" };
  return (
    <span
      className={`inline-flex items-center gap-1 text-[10px] uppercase font-bold px-2 py-0.5 border ${info.cls}`}
      data-testid={`payment-status-${status}`}
    >
      {info.label}
    </span>
  );
}

export default function InvoicePaymentsBlock({ invoice, onChanged }) {
  const { user, hasPermission } = useAuth();
  const userRole = (user?.role || "").toLowerCase();
  const isMedewerker = userRole === "medewerker";
  const canManage = !isMedewerker && hasPermission("orders:convert_financial");

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [registerOpen, setRegisterOpen] = useState(false);
  const [editPayment, setEditPayment] = useState(null);
  const [voidPayment, setVoidPayment] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.get(`/admin/sales-invoices/${invoice.id}/payments`, {
        params: { include_voided: true },
      });
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Laden betalingen mislukt");
    } finally {
      setLoading(false);
    }
  }, [invoice.id]);

  useEffect(() => { load(); }, [load]);

  const handleAfterMutation = async () => {
    await load();
    if (onChanged) onChanged();
  };

  const activePayments = (data?.payments || []).filter((p) => p.status === "active");
  const voidedPayments = (data?.payments || []).filter((p) => p.status === "voided");

  const totalInvoice = Number(data?.total_incl_vat ?? invoice.total_incl_vat ?? 0);
  const totalPaid = Number(data?.total_paid ?? invoice.total_paid ?? 0);
  const totalCredited = Number(invoice.total_credited || 0);
  const amountDue = totalCredited > 0 ? Math.max(0, totalInvoice - totalPaid - totalCredited) : Number(data?.amount_due ?? Math.max(0, totalInvoice - totalPaid));
  const overpaid = !!data?.overpaid || totalPaid > totalInvoice;
  const effectiveStatus =
    totalCredited > 0 || invoice.status === "geannuleerd" || invoice.status === "concept"
      ? invoice.status
      : totalPaid <= 0
      ? "open"
      : totalPaid >= totalInvoice
      ? "betaald"
      : "deels_betaald";

  return (
    <div className="space-y-3" data-testid="invoice-payments-block">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <CreditCard className="w-4 h-4 text-[#082c50]" />
          <h4 className="font-chivo font-black text-sm text-[#082c50] uppercase tracking-wide">
            Betalingen
          </h4>
          <PaymentStatusPill status={effectiveStatus} overpaid={overpaid} />
        </div>
        {canManage && (
          <Button
            size="sm"
            onClick={() => setRegisterOpen(true)}
            disabled={invoice.status === "geannuleerd" || invoice.deleted_at}
            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-chivo font-bold uppercase"
            data-testid="payment-register-btn"
          >
            <Plus className="w-3 h-3 mr-1" /> Betaling registreren
          </Button>
        )}
      </div>

      {/* Aggregaat-kaart */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
        <KPI label="Factuurtotaal" value={fmtEUR(totalInvoice, invoice.currency)} testid="payments-kpi-total" />
        <KPI label="Totaal betaald" value={fmtEUR(totalPaid, invoice.currency)} testid="payments-kpi-paid" cls={totalPaid > 0 ? "text-emerald-700" : ""} />
        <KPI
          label={overpaid ? "Overbetaald" : "Openstaand"}
          value={fmtEUR(overpaid ? totalPaid - totalInvoice : amountDue, invoice.currency)}
          testid="payments-kpi-due"
          cls={overpaid ? "text-purple-700" : amountDue > 0 ? "text-amber-800" : "text-emerald-700"}
        />
        <KPI label="Aantal betalingen" value={String(activePayments.length)} testid="payments-kpi-count" />
      </div>

      {/* Lijst actieve betalingen */}
      <div className="bg-white border border-gray-200 overflow-x-auto" data-testid="payments-active-table-wrapper">
        {loading ? (
          <div className="p-6 text-center text-gray-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Laden…</div>
        ) : activePayments.length === 0 ? (
          <div className="p-6 text-center text-gray-500" data-testid="payments-active-empty">
            Nog geen actieve betalingen geregistreerd voor deze factuur.
          </div>
        ) : (
          <table className="w-full text-xs min-w-[560px]">
            <thead className="bg-gray-50 text-[9px] uppercase text-gray-600">
              <tr>
                <th className="text-left p-2">Datum</th>
                <th className="text-left p-2">Betaalwijze</th>
                <th className="text-right p-2">Bedrag</th>
                <th className="text-left p-2 hidden sm:table-cell">Referentie</th>
                <th className="text-left p-2 hidden md:table-cell">Notitie</th>
                {canManage && <th className="text-right p-2">Acties</th>}
              </tr>
            </thead>
            <tbody>
              {activePayments.map((p) => (
                <tr key={p.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`payment-row-${p.id}`}>
                  <td className="p-2">{fmtDate(p.payment_date)}</td>
                  <td className="p-2">{methodLabel(p.payment_method, p.payment_method_other)}</td>
                  <td className="p-2 text-right font-mono font-bold">{fmtEUR(p.amount, p.currency || invoice.currency)}</td>
                  <td className="p-2 hidden sm:table-cell">{p.reference || "—"}</td>
                  <td className="p-2 hidden md:table-cell text-gray-600">{p.note || "—"}</td>
                  {canManage && (
                    <td className="p-2 text-right whitespace-nowrap">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditPayment(p)}
                        className="h-7 px-2 text-xs"
                        data-testid={`payment-edit-${p.id}`}
                      >
                        <Edit2 className="w-3 h-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setVoidPayment(p)}
                        className="h-7 px-2 text-xs text-red-600 hover:bg-red-50"
                        data-testid={`payment-void-${p.id}`}
                      >
                        <Ban className="w-3 h-3" />
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Voided lijst (collapsed) */}
      {voidedPayments.length > 0 && (
        <details className="bg-gray-50 border border-gray-200 p-3 text-xs" data-testid="payments-voided-section">
          <summary className="cursor-pointer font-bold text-gray-700 uppercase text-[10px] tracking-wide flex items-center gap-1">
            <Ban className="w-3 h-3" /> Geannuleerde betalingen ({voidedPayments.length})
          </summary>
          <div className="mt-2 overflow-x-auto">
            <table className="w-full text-xs min-w-[560px]">
              <thead className="text-[9px] uppercase text-gray-600">
                <tr>
                  <th className="text-left p-1.5">Datum</th>
                  <th className="text-left p-1.5">Betaalwijze</th>
                  <th className="text-right p-1.5">Bedrag</th>
                  <th className="text-left p-1.5">Reden void</th>
                  <th className="text-left p-1.5 hidden sm:table-cell">Door</th>
                </tr>
              </thead>
              <tbody>
                {voidedPayments.map((p) => (
                  <tr key={p.id} className="border-t border-gray-200 text-gray-600 line-through" data-testid={`payment-voided-row-${p.id}`}>
                    <td className="p-1.5">{fmtDate(p.payment_date)}</td>
                    <td className="p-1.5">{methodLabel(p.payment_method, p.payment_method_other)}</td>
                    <td className="p-1.5 text-right font-mono">{fmtEUR(p.amount, p.currency || invoice.currency)}</td>
                    <td className="p-1.5">{p.void_reason || "—"}</td>
                    <td className="p-1.5 hidden sm:table-cell">{p.voided_by_email || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}

      {!canManage && (
        <div className="text-[10px] text-gray-500 italic" data-testid="payments-readonly-notice">
          {isMedewerker
            ? "Alleen-lezen voor medewerkers — neem contact op met een Manager om betalingen te registreren."
            : "Onvoldoende rechten om betalingen te beheren."}
        </div>
      )}

      {registerOpen && (
        <PaymentRegisterDialog
          invoice={invoice}
          amountDue={amountDue}
          onClose={() => setRegisterOpen(false)}
          onSaved={async () => { setRegisterOpen(false); await handleAfterMutation(); }}
        />
      )}
      {editPayment && (
        <PaymentEditDialog
          invoice={invoice}
          payment={editPayment}
          onClose={() => setEditPayment(null)}
          onSaved={async () => { setEditPayment(null); await handleAfterMutation(); }}
        />
      )}
      {voidPayment && (
        <PaymentVoidDialog
          invoice={invoice}
          payment={voidPayment}
          onClose={() => setVoidPayment(null)}
          onSaved={async () => { setVoidPayment(null); await handleAfterMutation(); }}
        />
      )}
    </div>
  );
}

function KPI({ label, value, testid, cls = "" }) {
  return (
    <div className="bg-white border border-gray-200 p-2" data-testid={testid}>
      <div className="text-[9px] uppercase font-bold text-gray-500 tracking-wide">{label}</div>
      <div className={`text-sm font-mono font-bold ${cls || "text-[#082c50]"}`}>{value}</div>
    </div>
  );
}

// ============================================================
// REGISTER PAYMENT DIALOG
// ============================================================

function PaymentRegisterDialog({ invoice, amountDue, onClose, onSaved }) {
  const todayIso = new Date().toISOString().slice(0, 10);
  const suggested = amountDue > 0 ? amountDue : invoice.total_incl_vat || 0;
  const [amount, setAmount] = useState(suggested.toFixed(2));
  const [paymentDate, setPaymentDate] = useState(todayIso);
  const [method, setMethod] = useState("bank");
  const [methodOther, setMethodOther] = useState("");
  const [reference, setReference] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const handleSubmit = async () => {
    const num = parseFloat(String(amount).replace(",", "."));
    if (!num || num <= 0) {
      toast.error("Vul een positief bedrag in");
      return;
    }
    if (!paymentDate) {
      toast.error("Vul een betaaldatum in");
      return;
    }
    if (method === "anders" && !methodOther.trim()) {
      toast.error("Vul een omschrijving in voor betaalwijze 'Anders'");
      return;
    }
    setBusy(true);
    try {
      await adminApi.post(`/admin/sales-invoices/${invoice.id}/payments`, {
        amount: num,
        payment_date: paymentDate,
        payment_method: method,
        payment_method_other: method === "anders" ? methodOther.trim() : null,
        reference: reference.trim() || null,
        note: note.trim() || null,
      });
      toast.success("Betaling geregistreerd");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registreren mislukt");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-lg" data-testid="payment-register-dialog">
        <DialogHeader>
          <DialogTitle className="font-chivo font-black text-[#082c50] flex items-center gap-2">
            <Receipt className="w-5 h-5" /> Betaling registreren
          </DialogTitle>
          <DialogDescription>
            Factuur <span className="font-mono font-bold">{invoice.invoice_number}</span> · openstaand{" "}
            <span className="font-mono font-bold">{fmtEUR(amountDue, invoice.currency)}</span>
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <div>
            <Label className="text-[10px] uppercase font-bold">Bedrag *</Label>
            <Input
              type="number"
              step="0.01"
              min="0.01"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="rounded-xl mt-1 font-mono text-right"
              data-testid="payment-form-amount"
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase font-bold">Betaaldatum *</Label>
            <Input
              type="date"
              value={paymentDate}
              onChange={(e) => setPaymentDate(e.target.value)}
              className="rounded-xl mt-1"
              data-testid="payment-form-date"
            />
          </div>
          <div className="col-span-1 sm:col-span-2">
            <Label className="text-[10px] uppercase font-bold">Betaalwijze *</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger className="rounded-xl mt-1" data-testid="payment-form-method">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAYMENT_METHODS.map((m) => (
                  <SelectItem key={m.value} value={m.value} data-testid={`payment-method-${m.value}`}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {method === "anders" && (
            <div className="col-span-1 sm:col-span-2">
              <Label className="text-[10px] uppercase font-bold">Omschrijving betaalwijze *</Label>
              <Input
                value={methodOther}
                onChange={(e) => setMethodOther(e.target.value)}
                placeholder="bv. cheque, crypto, etc."
                className="rounded-xl mt-1"
                maxLength={120}
                data-testid="payment-form-method-other"
              />
            </div>
          )}
          <div className="col-span-1 sm:col-span-2">
            <Label className="text-[10px] uppercase font-bold">Referentie</Label>
            <Input
              value={reference}
              onChange={(e) => setReference(e.target.value)}
              placeholder="bv. TX-nummer, kasbon-nr, mollie-id…"
              className="rounded-xl mt-1"
              maxLength={200}
              data-testid="payment-form-reference"
            />
          </div>
          <div className="col-span-1 sm:col-span-2">
            <Label className="text-[10px] uppercase font-bold">Notitie</Label>
            <Textarea
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Optionele interne notitie"
              className="rounded-xl mt-1"
              maxLength={500}
              data-testid="payment-form-note"
            />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy} className="rounded-xl text-xs" data-testid="payment-form-cancel">
            Annuleren
          </Button>
          <Button onClick={handleSubmit} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs" data-testid="payment-form-submit">
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
            {busy ? "Registreren…" : "Betaling registreren"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// EDIT PAYMENT DIALOG
// ============================================================

function PaymentEditDialog({ invoice, payment, onClose, onSaved }) {
  const [amount, setAmount] = useState(String(payment.amount ?? 0));
  const [paymentDate, setPaymentDate] = useState((payment.payment_date || "").slice(0, 10));
  const [method, setMethod] = useState(payment.payment_method || "bank");
  const [methodOther, setMethodOther] = useState(payment.payment_method_other || "");
  const [reference, setReference] = useState(payment.reference || "");
  const [note, setNote] = useState(payment.note || "");
  const [busy, setBusy] = useState(false);

  const handleSubmit = async () => {
    const num = parseFloat(String(amount).replace(",", "."));
    if (!num || num <= 0) { toast.error("Vul een positief bedrag in"); return; }
    if (!paymentDate) { toast.error("Vul een betaaldatum in"); return; }
    if (method === "anders" && !methodOther.trim()) {
      toast.error("Vul een omschrijving in voor betaalwijze 'Anders'");
      return;
    }
    setBusy(true);
    try {
      await adminApi.patch(`/admin/sales-invoices/${invoice.id}/payments/${payment.id}`, {
        amount: num,
        payment_date: paymentDate,
        payment_method: method,
        payment_method_other: method === "anders" ? methodOther.trim() : null,
        reference: reference.trim() || null,
        note: note.trim() || null,
      });
      toast.success("Betaling bijgewerkt");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Bijwerken mislukt");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-lg" data-testid="payment-edit-dialog">
        <DialogHeader>
          <DialogTitle className="font-chivo font-black text-[#082c50] flex items-center gap-2">
            <Edit2 className="w-5 h-5" /> Betaling bewerken
          </DialogTitle>
          <DialogDescription>
            Wijzigen heeft direct invloed op de betaalstatus van factuur{" "}
            <span className="font-mono font-bold">{invoice.invoice_number}</span>.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <div>
            <Label className="text-[10px] uppercase font-bold">Bedrag *</Label>
            <Input type="number" step="0.01" min="0.01" value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="rounded-xl mt-1 font-mono text-right" data-testid="payment-edit-amount" />
          </div>
          <div>
            <Label className="text-[10px] uppercase font-bold">Betaaldatum *</Label>
            <Input type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} className="rounded-xl mt-1" data-testid="payment-edit-date" />
          </div>
          <div className="col-span-1 sm:col-span-2">
            <Label className="text-[10px] uppercase font-bold">Betaalwijze *</Label>
            <Select value={method} onValueChange={setMethod}>
              <SelectTrigger className="rounded-xl mt-1" data-testid="payment-edit-method"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PAYMENT_METHODS.map((m) => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {method === "anders" && (
            <div className="col-span-1 sm:col-span-2">
              <Label className="text-[10px] uppercase font-bold">Omschrijving betaalwijze *</Label>
              <Input value={methodOther} onChange={(e) => setMethodOther(e.target.value)} className="rounded-xl mt-1" maxLength={120} data-testid="payment-edit-method-other" />
            </div>
          )}
          <div className="col-span-1 sm:col-span-2">
            <Label className="text-[10px] uppercase font-bold">Referentie</Label>
            <Input value={reference} onChange={(e) => setReference(e.target.value)} className="rounded-xl mt-1" maxLength={200} data-testid="payment-edit-reference" />
          </div>
          <div className="col-span-1 sm:col-span-2">
            <Label className="text-[10px] uppercase font-bold">Notitie</Label>
            <Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} className="rounded-xl mt-1" maxLength={500} data-testid="payment-edit-note" />
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy} className="rounded-xl text-xs">Annuleren</Button>
          <Button onClick={handleSubmit} disabled={busy} className="bg-[#082c50] text-white rounded-xl text-xs" data-testid="payment-edit-save">
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <CheckCircle2 className="w-3 h-3 mr-1" />}
            {busy ? "Opslaan…" : "Wijzigingen opslaan"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================
// VOID PAYMENT DIALOG
// ============================================================

function PaymentVoidDialog({ invoice, payment, onClose, onSaved }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const handleConfirm = async () => {
    if (!reason.trim()) {
      toast.error("Reden is verplicht bij voiden");
      return;
    }
    setBusy(true);
    try {
      await adminApi.post(`/admin/sales-invoices/${invoice.id}/payments/${payment.id}/void`, {
        void_reason: reason.trim(),
      });
      toast.success("Betaling gevoid");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Voiden mislukt");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-md" data-testid="payment-void-dialog">
        <DialogHeader>
          <DialogTitle className="font-chivo font-black text-red-700 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" /> Betaling voiden?
          </DialogTitle>
          <DialogDescription>
            Deze betaling van <span className="font-mono font-bold">{fmtEUR(payment.amount, payment.currency || invoice.currency)}</span> op{" "}
            <span className="font-mono">{fmtDate(payment.payment_date)}</span> wordt op{" "}
            <strong>gevoid</strong> gezet. De betaalstatus van factuur{" "}
            <span className="font-mono font-bold">{invoice.invoice_number}</span> wordt automatisch
            herberekend.
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label className="text-[10px] uppercase font-bold">Reden *</Label>
          <Textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Bijv. verkeerd geboekt, gestorneerd, dubbel ingevoerd…"
            rows={3}
            maxLength={500}
            className="rounded-xl mt-1 text-xs"
            data-testid="payment-void-reason"
          />
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={busy} className="rounded-xl text-xs">Terug</Button>
          <Button onClick={handleConfirm} disabled={busy} className="bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs" data-testid="payment-void-confirm">
            {busy ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Ban className="w-3 h-3 mr-1" />}
            {busy ? "Voiden…" : "Ja, betaling voiden"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
